import { Splide } from '@splidejs/splide';

/**
 * SlidePickup
 * ピックアップのカルーセル（ループ）＝サイト共通。SP＝中央1枚＋両隣ちら見、PC＝中央フォーカス＋ちら見。
 * 各pickup（ノウハウ/セミナー/事例/資料DL）で共用。中身はカード or バナー画像。複数インスタンス対応。
 *
 * data属性でカード幅・挙動を切替：
 *   data-fixed-width-sp / -pc（既定 29.5rem / 37.3rem。幅広は 60rem）
 *   data-gap-sp / -pc（既定 2.4rem / 4rem）
 *   data-autoplay="true"（自動ループ。既定オフ＝手動）
 *   data-arrows="false" / data-pagination="false"（操作ボタンを消す。既定オン）
 *   data-drag（既定＝手動時on/自動時off）
 *
 * @example
 * <div class="p-pickup-slider splide js-slide-pickup" data-fixed-width-pc="60rem"
 *      data-autoplay="true" data-arrows="false" data-pagination="false">…</div>
 */
export default class SlidePickup {
  constructor(options = {}) {
    this.selector = options.selector || '.js-slide-pickup';
    document.querySelectorAll(this.selector).forEach((el) => this.setup(el));
  }

  /**
   * @param {HTMLElement} el
   */
  setup(el) {
    try {
      // モバイルファースト（mediaQuery:'min'）。中央寄せ＋両側ちら見（両端まで見えるブリード型）。
      // カード幅は data 属性で可変（既定＝詳細用 SP29.5/PC37.3。一覧は data-fixed-width-pc="60rem"）。
      // focus:'center' 固定＝両端にブリードカードが覗く。
      const widthSp = el.dataset.fixedWidthSp || '29.5rem';
      const widthPc = el.dataset.fixedWidthPc || '37.3rem';
      const gapSp = el.dataset.gapSp || '2.4rem';
      const gapPc = el.dataset.gapPc || '4rem';
      const autoplay = el.dataset.autoplay === 'true';
      const arrows = el.dataset.arrows !== 'false';
      // 1枚のときはインジケータ非表示
      const pagination = el.dataset.pagination !== 'false' && el.querySelectorAll('.splide__slide').length > 1;
      const drag = el.dataset.drag ? el.dataset.drag === 'true' : !autoplay;

      const splide = new Splide(el, {
        type: 'loop',
        perPage: 1,
        perMove: 1,
        focus: 'center',
        fixedWidth: widthSp,
        gap: gapSp,
        arrows,
        pagination,
        drag,
        mediaQuery: 'min',
        breakpoints: {
          768: {
            fixedWidth: widthPc,
            gap: gapPc,
          },
        },
        // 自動ループ（資料DLピックアップ等）。操作ボタンは data 属性で別途オフにする想定
        ...(autoplay ? { autoplay: true, interval: 3000, speed: 800, pauseOnHover: false, pauseOnFocus: false } : {}),
      });
      splide.mount();

      // 画面外では自動再生を停止（SlideFade同様。オフスクリーンでの無駄なスライド切替を防ぐ）
      if (autoplay && 'IntersectionObserver' in window) {
        this.setupViewportPause(el, splide);
      }
    } catch (error) {
      console.error('SlidePickup: Failed to initialize Splide', error);
    }
  }

  /**
   * 画面外で autoplay を一時停止／画面内で再開（IntersectionObserver）。
   * @param {HTMLElement} el
   * @param {import('@splidejs/splide').Splide} splide
   */
  setupViewportPause(el, splide) {
    const ap = splide.Components.Autoplay;
    if (!ap) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          ap.play();
        } else {
          ap.pause();
        }
      });
    }, { threshold: 0 });

    observer.observe(el);
  }
}
